import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/reduxHooks';
import useTranslate from '../../../../../hooks/useTranslate';
import { RADIOLGICAL_EQUIPMENT_DRAWER_ACTION_VALUES } from '../components/drawers';
import {
    useDeleteCarcinogenicEquipmentMutation,
    useEditCarcinogenicReportEstablishmentMutation
} from '../../../../../services/carcinogenicApi';
import { enqueueSnackbar } from 'notistack';
import { showAlert } from '../../../../../features/slices/applicationSlice';
import { ALERT_CONFIRM_TEXT, ERROR_CONTACT_SUPPORT } from '../../../../../constants/values';
import useAlerts from './useAlerts';
import { handleErrors } from '../../../../../utils/common';

const useRadiologicalEquipmentModule = ({ selectedRadiologicalEquipment, isEditing }: any) => {
    const dispatch = useAppDispatch();
    const [editCarcinogenicReport, { isLoading: isEditingCarcinogenicReportRadiologicalCheck }] =
        useEditCarcinogenicReportEstablishmentMutation();

    const selectedEstablishmentId = useAppSelector(
        (state) => state.establishment.selectedEstablishmentId
    );

    const selectedCarcinogenicReportId = useAppSelector(
        (state) => state?.carcinogenicReport?.selectedCarcinogenicReportId
    );

    const establishmentData = useAppSelector(
        (state) => state?.carcinogenicReport?.carcinogenicReport?.establecimiento
    );

    const carcinogenicReport = useAppSelector(
        (state) => state?.carcinogenicReport?.carcinogenicReport
    );

    const providersEmptyDeclarationCheckState = useAppSelector(
        (state) => state?.carcinogenicReport?.carcinogenicReport?.proveedoresCheck
    );

    const radiologicalEmptyDeclarationCheckState = useAppSelector(
        (state) => state?.carcinogenicReport?.carcinogenicReport?.radiologicosCheck
    );

    const handleEditEmptyRadiologicalCheck = async () => {
        try {
            if (!selectedCarcinogenicReportId || !selectedEstablishmentId) return;
            const response: any = await editCarcinogenicReport({
                reportId: selectedCarcinogenicReportId,
                body: {
                    idEstablecimiento: selectedEstablishmentId,
                    establecimiento: establishmentData,
                    proveedoresCheck: providersEmptyDeclarationCheckState,
                    radiologicosCheck: !radiologicalEmptyDeclarationCheckState
                }
            });
            if (response?.error) {
                if (response?.error?.data?.statusCode === 400) {
                    handleErrors(response?.error);
                    return;
                }
                enqueueSnackbar(t('enqueues.toggleCheck.error'), {
                    variant: 'error'
                });
            } else {
                enqueueSnackbar(t('enqueues.toggleCheck.success'), {
                    variant: 'success'
                });
            }
        } catch (error) {
            dispatch(
                showAlert({
                    title: 'No se pudo actualizar la declaración de equipos radiológicos',
                    message: ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    const [open, setOpen] = useState<string>('');
    const [selected, setSelected] = useState<any | null>(null);

    const { t } = useTranslation('carcinogenicReport', {
        keyPrefix: 'modules.radiologicalEquipment'
    });
    const { translate } = useTranslate(t);

    const { showConfirmDeleteEquipmentAlert } = useAlerts({ translate });

    const handleEditIconClick = (item: any) => {
        setSelected(item);
        setOpen(RADIOLGICAL_EQUIPMENT_DRAWER_ACTION_VALUES.EDIT_RADIOLGICAL_EQUIPMENT);
    };

    const [deleteEquipment, { isLoading: isDeletingEquipment }] =
        useDeleteCarcinogenicEquipmentMutation();

    const handleDeleteEquipmentIconClick = async (selectedEquipment: any) => {
        showConfirmDeleteEquipmentAlert({
            confirmAction: () => handleDeleteEquipment(selectedEquipment)
        });
    };

    const handleDeleteEquipment = async (selectedEquipment: any) => {
        try {
            if (!selectedCarcinogenicReportId || !selectedEquipment?.id) return;
            const response: any = await deleteEquipment({
                reportId: selectedCarcinogenicReportId,
                substanceId: selectedEquipment?.sustancia?.id || 0,
                equipmentId: selectedEquipment.id
            });
            if (response?.error) {
                enqueueSnackbar(t('enqueues.deleteEquipment.error'), {
                    variant: 'error'
                });
            } else {
                enqueueSnackbar(t('enqueues.deleteEquipment.success'), {
                    variant: 'success'
                });
            }
        } catch (error) {
            dispatch(
                showAlert({
                    title: 'No se pudo eliminar el equipo radiológico',
                    message: ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    const handleClickNewEquipmentButton = () => {
        setSelected(null);
        setOpen(RADIOLGICAL_EQUIPMENT_DRAWER_ACTION_VALUES.NEW_RADIOLGICAL_EQUIPMENT);
    };

    return {
        carcinogenicReport,
        selectedCarcinogenicReportId,
        selected,
        open,
        isDeletingEquipment,
        radiologicalEmptyDeclarationCheckState,
        isEditingCarcinogenicReportRadiologicalCheck,
        handleEditEmptyRadiologicalCheck,
        handleDeleteEquipmentIconClick,
        handleClickNewEquipmentButton,
        handleEditIconClick,
        setSelected,
        setOpen,
        translate
    };
};

export default useRadiologicalEquipmentModule;
